import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, TrendingUp, ShoppingCart, BarChart3, Users, Package,
  Loader2, Trophy, ArrowUpRight
} from 'lucide-react';
import { api } from '../App';
import { getCategoryLabel } from '../lib/categories';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All Time' },
];

const CHART_COLORS = ['#F0BD13', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

function StatCard({ label, value, prefix = '$', icon: Icon, color = 'text-white', accent = false }) {
  return (
    <div className={`p-5 bg-surface-card border ${accent ? 'border-gold/30' : 'border-border-subtle'}`} data-testid={`revenue-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${accent ? 'text-gold' : 'text-zinc-600'}`} />}
      </div>
      <p className={`text-2xl font-clash font-bold ${color}`}>
        {prefix}{typeof value === 'number' ? (
          prefix === '' ? value.toLocaleString() : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        ) : value}
      </p>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-card border border-border-subtle p-3 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm" style={{ color: p.color }}>
          {p.name}: {p.name === 'Transactions' ? p.value : `$${p.value.toLocaleString()}`}
        </p>
      ))}
    </div>
  );
}

export default function RevenueDashboard() {
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/revenue?period=${period}`);
      setData(d);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="space-y-8" data-testid="revenue-dashboard">
      {/* Period Selector */}
      <div className="flex items-center gap-2">
        {PERIODS.map(p => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
              period === p.value
                ? 'bg-gold text-black border-gold font-semibold'
                : 'bg-surface-card text-zinc-400 border-border-subtle hover:border-zinc-600'
            }`}
            data-testid={`revenue-period-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Revenue Summary */}
      <div>
        <h3 className="font-clash text-lg font-medium text-white mb-4">Platform Revenue</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Platform Revenue" value={s.platform_revenue || 0} icon={DollarSign} color="text-gold" accent />
          <StatCard label="Buyer Fees" value={s.total_buyer_fees || 0} icon={ShoppingCart} color="text-emerald-400" />
          <StatCard label="Seller Commissions" value={s.total_seller_fees || 0} icon={TrendingUp} color="text-blue-400" />
          <StatCard label="Seller Payouts" value={s.total_seller_payouts || 0} icon={Users} color="text-zinc-300" />
        </div>
      </div>

      {/* Transaction Volume */}
      <div>
        <h3 className="font-clash text-lg font-medium text-white mb-4">Transaction Volume</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Completed Orders" value={s.order_count || 0} prefix="" icon={Package} />
          <StatCard label="Gross Sales" value={s.total_gross_sales || 0} icon={BarChart3} />
          <StatCard label="Avg Order Value" value={s.avg_order_value || 0} icon={TrendingUp} />
        </div>
      </div>

      {/* Charts Row */}
      {data?.daily?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Over Time */}
          <div className="p-6 bg-surface-card border border-border-subtle">
            <h4 className="font-clash text-base font-medium text-white mb-4">Revenue Over Time</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily}>
                  <defs>
                    <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F0BD13" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F0BD13" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#F0BD13" fill="url(#goldGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Transactions Over Time */}
          <div className="p-6 bg-surface-card border border-border-subtle">
            <h4 className="font-clash text-base font-medium text-white mb-4">Transactions Over Time</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="transactions" name="Transactions" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Category Performance + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Table */}
        <div className="p-6 bg-surface-card border border-border-subtle">
          <h4 className="font-clash text-base font-medium text-white mb-4">Category Performance</h4>
          {data?.by_category?.length > 0 ? (
            <div className="space-y-3">
              {data.by_category.map((cat, idx) => (
                <div key={cat.category} className="flex items-center gap-3">
                  <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{getCategoryLabel(cat.category)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm text-zinc-300">{cat.orders} orders</p>
                    <p className="text-xs text-gold">${cat.volume.toLocaleString()} volume</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 py-4">No category data yet</p>
          )}
        </div>

        {/* Pie Chart */}
        <div className="p-6 bg-surface-card border border-border-subtle">
          <h4 className="font-clash text-base font-medium text-white mb-4">Revenue by Category</h4>
          {data?.by_category?.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.by_category.map(c => ({ name: getCategoryLabel(c.category), value: c.revenue }))}
                    cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                    paddingAngle={2} dataKey="value"
                  >
                    {data.by_category.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-surface-card border border-border-subtle p-3 shadow-xl">
                          <p className="text-sm text-white">{payload[0].name}: ${payload[0].value.toLocaleString()}</p>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 py-4">No revenue data yet</p>
          )}
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="p-6 bg-surface-card border border-border-subtle">
        <h4 className="font-clash text-base font-medium text-white mb-4">Fee Breakdown</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Buyer Service Fees ({s.buyer_fee_pct || 3}%)</p>
            <p className="text-xl font-clash font-bold text-emerald-400">${(s.total_buyer_fees || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Seller Commissions ({s.seller_fee_pct || 7}%)</p>
            <p className="text-xl font-clash font-bold text-blue-400">${(s.total_seller_fees || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Net Seller Payouts</p>
            <p className="text-xl font-clash font-bold text-zinc-300">${(s.total_seller_payouts || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Top Sellers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 bg-surface-card border border-border-subtle">
          <h4 className="font-clash text-base font-medium text-white mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-gold" /> Top Sellers
          </h4>
          {data?.top_sellers?.length > 0 ? (
            <div className="space-y-3">
              {data.top_sellers.map((seller, idx) => (
                <div key={seller.seller_id} className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-gold text-black' : idx === 1 ? 'bg-zinc-400 text-black' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-surface-elevated text-zinc-400'
                  }`}>{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{seller.seller_name}</p>
                    <p className="text-xs text-zinc-500">{seller.orders} orders</p>
                  </div>
                  <p className="text-sm text-gold font-clash font-semibold">${seller.gross.toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 py-4">No sellers data yet</p>
          )}
        </div>

        {/* Top Listings */}
        <div className="p-6 bg-surface-card border border-border-subtle">
          <h4 className="font-clash text-base font-medium text-white mb-4 flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-gold" /> Top Listings
          </h4>
          {data?.top_listings?.length > 0 ? (
            <div className="space-y-3">
              {data.top_listings.map((listing, idx) => (
                <div key={`${listing.listing_id}-${idx}`} className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-gold text-black' : 'bg-surface-elevated text-zinc-400'
                  }`}>{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{listing.listing_title}</p>
                    <p className="text-xs text-zinc-500">Sold to: {listing.buyer_name}</p>
                  </div>
                  <p className="text-sm text-gold font-clash font-semibold">${listing.item_price.toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 py-4">No listings data yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
